import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, IconButton, InputBase, Tooltip } from "@material-ui/core";
import {
    Close,
    NavigateBefore,
    NavigateNext,
    Send,
    Videocam,
    Audiotrack,
    InsertDriveFile,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import AudioModal from "../AudioModal";

// Prévia antes de enviar no padrão do WhatsApp Web: palco escuro com a mídia grande,
// legenda embaixo, miniaturas quando há vários arquivos e botão redondo de enviar.
// O palco é escuro nos dois temas (como no WhatsApp), por isso as cores são fixas.
const stage = {
    bg: "#0b141a",
    bar: "#111b21",
    field: "#2a3942",
    text: "#e9edef",
    muted: "#8696a0",
    line: "rgba(134,150,160,0.18)",
    accent: "#00a884",
    accentHover: "#06cf9c",
};

const EXT_COLORS = {
    pdf: "#e5484d",
    doc: "#2b7de9", docx: "#2b7de9", odt: "#2b7de9", rtf: "#2b7de9",
    xls: "#1f9d55", xlsx: "#1f9d55", xlsm: "#1f9d55", csv: "#1f9d55", ods: "#1f9d55",
    ppt: "#e8710a", pptx: "#e8710a", odp: "#e8710a",
    zip: "#8d6e63", rar: "#8d6e63", "7z": "#8d6e63",
};

const useStyles = makeStyles((theme) => ({
    paper: {
        width: "min(880px, calc(100vw - 32px))",
        maxWidth: "none",
        height: "min(680px, calc(100vh - 32px))",
        margin: 16,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: 0,
        borderRadius: 12,
        backgroundColor: stage.bg,
        color: stage.text,
        [theme.breakpoints.down("xs")]: {
            width: "100vw",
            height: "100%",
            maxHeight: "none",
            margin: 0,
            borderRadius: 0,
        },
    },
    topBar: {
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 56,
        padding: "0 12px 0 8px",
        backgroundColor: stage.bar,
    },
    iconButton: {
        width: 40,
        height: 40,
        padding: 0,
        borderRadius: "50%",
        color: stage.muted,
        "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: stage.text },
        "&.Mui-disabled": { color: "rgba(134,150,160,0.35)" },
    },
    fileTitle: {
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        fontSize: 14.5,
        fontWeight: 500,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    counter: {
        flex: "none",
        minWidth: 40,
        textAlign: "right",
        fontSize: 12.5,
        color: stage.muted,
        fontVariantNumeric: "tabular-nums",
    },
    stage: {
        flex: 1,
        minHeight: 0,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px 56px",
        [theme.breakpoints.down("xs")]: { padding: "12px 12px" },
    },
    media: {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        borderRadius: 4,
        boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
    },
    nav: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        backgroundColor: "rgba(17,27,33,0.72)",
        "&:hover": { backgroundColor: "rgba(42,57,66,0.95)", color: stage.text },
        [theme.breakpoints.down("xs")]: { display: "none" },
    },
    navPrev: { left: 10 },
    navNext: { right: 10 },
    // áudio usa o player do sistema, que segue o tema: precisa de uma superfície do tema por baixo
    audioCard: {
        padding: "12px 14px",
        borderRadius: 10,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        maxWidth: "100%",
    },
    fileCard: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        maxWidth: "100%",
        textAlign: "center",
    },
    fileBadge: {
        width: 72,
        height: 88,
        borderRadius: 8,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingBottom: 12,
        color: "#ffffff",
        fontSize: 15,
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        // canto dobrado, como o cartão de arquivo da conversa
        clipPath: "polygon(0 0, 68% 0, 100% 24%, 100% 100%, 0 100%)",
    },
    fileName: {
        maxWidth: 420,
        fontSize: 15,
        fontWeight: 500,
        wordBreak: "break-word",
    },
    fileMeta: {
        fontSize: 13,
        color: stage.muted,
    },
    captionRow: {
        flex: "none",
        display: "flex",
        justifyContent: "center",
        padding: "0 16px 12px",
    },
    caption: {
        width: "min(640px, 100%)",
        padding: "9px 14px",
        borderRadius: 8,
        backgroundColor: stage.field,
        color: stage.text,
        fontSize: 15,
        lineHeight: "20px",
        "& textarea::placeholder": { color: stage.muted, opacity: 1 },
    },
    bottomBar: {
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 84,
        padding: "10px 16px",
        borderTop: `1px solid ${stage.line}`,
        backgroundColor: stage.bar,
    },
    thumbs: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        justifyContent: "center",
        gap: 8,
        overflowX: "auto",
        padding: "2px 2px",
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(134,150,160,0.35)", borderRadius: 3 },
    },
    // com muitos arquivos a fileira rola; centralizar cortaria os primeiros
    thumbsMany: { justifyContent: "flex-start" },
    thumb: {
        flex: "none",
        width: 52,
        height: 52,
        padding: 0,
        border: "2px solid transparent",
        borderRadius: 8,
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: stage.field,
        color: stage.muted,
        opacity: 0.7,
        transition: "opacity 120ms ease, border-color 120ms ease",
        "&:hover": { opacity: 1 },
        "&:focus-visible": { outline: `2px solid ${stage.accentHover}`, outlineOffset: 1 },
        "& img": { width: "100%", height: "100%", objectFit: "cover" },
        "& span": { fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: stage.text },
    },
    thumbActive: {
        opacity: 1,
        borderColor: stage.accent,
    },
    sendWrap: {
        flex: "none",
        position: "relative",
        marginLeft: "auto",
    },
    send: {
        width: 52,
        height: 52,
        padding: 0,
        borderRadius: "50%",
        backgroundColor: stage.accent,
        color: "#0b141a",
        "&:hover": { backgroundColor: stage.accentHover },
        "& .MuiSvgIcon-root": { fontSize: 24, marginLeft: 3 },
    },
    sendCount: {
        position: "absolute",
        top: -4,
        right: -4,
        minWidth: 20,
        height: 20,
        padding: "0 5px",
        borderRadius: 10,
        backgroundColor: stage.text,
        color: stage.bg,
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: "20px",
        textAlign: "center",
        pointerEvents: "none",
    },
}));

const kindOf = (file) => {
    const type = (file && file.type) || "";
    if (type.startsWith("image")) return "image";
    if (type.startsWith("video")) return "video";
    if (type.startsWith("audio")) return "audio";
    return "file";
};

const extOf = (file) => {
    const name = (file && file.name) || "";
    const dot = name.lastIndexOf(".");
    return dot > 0 && name.length - dot <= 6 ? name.slice(dot + 1).toLowerCase() : "";
};

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
};

const MessageUploadMedias = ({ isOpen, files, onClose, onSend, onCancelSelection }) => {
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [captions, setCaptions] = useState(files.map(() => ""));

    // Uma URL por arquivo com prévia, criada uma vez e revogada ao trocar a seleção ou fechar
    // (antes criava uma nova a cada renderização e nunca liberava).
    const previewUrls = useMemo(
        () => files.map((file) => (kindOf(file) === "file" ? null : URL.createObjectURL(file))),
        [files]
    );
    useEffect(() => {
        return () => {
            previewUrls.forEach((url) => url && URL.revokeObjectURL(url));
        };
    }, [previewUrls]);

    const safeIndex = Math.min(currentIndex, Math.max(files.length - 1, 0));
    const currentFile = files[safeIndex];
    const currentCaption = captions[safeIndex] || "";

    const handlePrev = () => {
        if (safeIndex > 0) setCurrentIndex(safeIndex - 1);
    };

    const handleNext = () => {
        if (safeIndex < files.length - 1) setCurrentIndex(safeIndex + 1);
    };

    const handleCaptionChange = (e) => {
        const value = e.target.value;
        setCaptions((prevCaptions) => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[safeIndex] = value;
            return updatedCaptions;
        });
    };

    const handleSend = () => {
        const selectedMedias = files.map((file, index) => ({
            file,
            caption: captions[index] || "",
        }));
        onSend(selectedMedias);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && event.shiftKey) {
            return;
        }
        switch (event.key) {
            case "Escape":
                onCancelSelection();
                break;
            case "Enter":
                event.preventDefault();
                handleSend();
                break;
            // com legenda digitada as setas movem o cursor; sem legenda trocam de arquivo
            case "ArrowRight":
                if (!currentCaption) handleNext();
                break;
            case "ArrowLeft":
                if (!currentCaption) handlePrev();
                break;
            default:
                break;
        }
    };

    const renderStage = () => {
        if (!currentFile) return null;
        const kind = kindOf(currentFile);
        const url = previewUrls[safeIndex];
        if (kind === "image") {
            return <img alt={currentFile.name} src={url} className={classes.media} />;
        }
        if (kind === "video") {
            return (
                <video
                    key={url}
                    src={url}
                    controls
                    volume={localStorage.getItem("volume")}
                    className={classes.media}
                />
            );
        }
        if (kind === "audio") {
            return (
                <div className={classes.audioCard}>
                    <AudioModal url={url} />
                </div>
            );
        }
        const ext = extOf(currentFile);
        return (
            <div className={classes.fileCard}>
                <div
                    className={classes.fileBadge}
                    style={{ backgroundColor: EXT_COLORS[ext] || "#667781" }}
                >
                    {ext || "arq"}
                </div>
                <div className={classes.fileName}>{currentFile.name}</div>
                <div className={classes.fileMeta}>
                    {[formatSize(currentFile.size), "Sem prévia disponível"].filter(Boolean).join(" · ")}
                </div>
            </div>
        );
    };

    const renderThumb = (file, index) => {
        const kind = kindOf(file);
        let content;
        if (kind === "image") content = <img alt="" src={previewUrls[index]} />;
        else if (kind === "video") content = <Videocam />;
        else if (kind === "audio") content = <Audiotrack />;
        else content = extOf(file) ? <span>{extOf(file)}</span> : <InsertDriveFile />;
        return (
            <button
                type="button"
                key={`${file.name}-${index}`}
                className={clsx(classes.thumb, index === safeIndex && classes.thumbActive)}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Arquivo ${index + 1} de ${files.length}: ${file.name}`}
                aria-current={index === safeIndex ? "true" : undefined}
                title={file.name}
            >
                {content}
            </button>
        );
    };

    const many = files.length > 1;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            classes={{ paper: classes.paper }}
            aria-label="Prévia dos arquivos a enviar"
        >
            <div className={classes.topBar}>
                <Tooltip title="Descartar">
                    <IconButton
                        className={classes.iconButton}
                        onClick={onCancelSelection}
                        aria-label="Descartar arquivos"
                    >
                        <Close />
                    </IconButton>
                </Tooltip>
                <div className={classes.fileTitle} title={currentFile ? currentFile.name : ""}>
                    {currentFile ? currentFile.name : ""}
                </div>
                <div className={classes.counter}>
                    {many ? `${safeIndex + 1} de ${files.length}` : ""}
                </div>
            </div>

            <div className={classes.stage}>
                {many && (
                    <IconButton
                        className={clsx(classes.iconButton, classes.nav, classes.navPrev)}
                        onClick={handlePrev}
                        disabled={safeIndex === 0}
                        aria-label="Arquivo anterior"
                    >
                        <NavigateBefore />
                    </IconButton>
                )}
                {renderStage()}
                {many && (
                    <IconButton
                        className={clsx(classes.iconButton, classes.nav, classes.navNext)}
                        onClick={handleNext}
                        disabled={safeIndex === files.length - 1}
                        aria-label="Próximo arquivo"
                    >
                        <NavigateNext />
                    </IconButton>
                )}
            </div>

            <div className={classes.captionRow}>
                <InputBase
                    className={classes.caption}
                    placeholder="Adicione uma legenda"
                    multiline
                    rowsMax={4}
                    value={currentCaption}
                    onChange={handleCaptionChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    inputProps={{ "aria-label": "Legenda do arquivo" }}
                />
            </div>

            <div className={classes.bottomBar}>
                <div className={clsx(classes.thumbs, files.length > 8 && classes.thumbsMany)}>
                    {many && files.map(renderThumb)}
                </div>
                <div className={classes.sendWrap}>
                    <Tooltip title="Enviar">
                        <IconButton className={classes.send} onClick={handleSend} aria-label="Enviar">
                            <Send />
                        </IconButton>
                    </Tooltip>
                    {many && <span className={classes.sendCount}>{files.length}</span>}
                </div>
            </div>
        </Dialog>
    );
};

export default MessageUploadMedias;
